import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSystemStatus } from '@/hooks/useSystemStatus';

const DISMISS_KEY = 'larkpm:dismissed-notice';

function noticeFingerprint(message: string, scheduledAt?: string): string {
  return `${message}::${scheduledAt ?? ''}`;
}

export function SystemStatusBanner() {
  const { status } = useSystemStatus();
  const [dismissed, setDismissed] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY);
    } catch {
      return null;
    }
  });

  const notice = status?.deployNotice;
  const fingerprint = useMemo(
    () => (notice?.message ? noticeFingerprint(notice.message, notice.scheduledAt) : ''),
    [notice?.message, notice?.scheduledAt],
  );

  useEffect(() => {
    if (!fingerprint) return;
    try {
      const stored = sessionStorage.getItem(DISMISS_KEY);
      if (stored && stored !== fingerprint) {
        sessionStorage.removeItem(DISMISS_KEY);
        setDismissed(null);
      }
    } catch {
      /* noop */
    }
  }, [fingerprint]);

  if (!notice?.message || dismissed === fingerprint) {
    return null;
  }

  const severity = notice.severity ?? 'info';
  const Icon = severity === 'info' ? Info : AlertTriangle;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, fingerprint);
    } catch {
      /* noop */
    }
    setDismissed(fingerprint);
  };

  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-3 border-b px-4 py-2.5 text-sm',
        severity === 'urgent' && 'border-destructive/30 bg-destructive/10 text-destructive',
        severity === 'warning' && 'border-amber-500/30 bg-amber-500/10 text-amber-950 dark:text-amber-100',
        severity === 'info' && 'border-border bg-muted/60 text-foreground',
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{notice.message}</p>
        {notice.scheduledAt && (
          <p className="mt-0.5 text-xs opacity-80">
            Scheduled: {new Date(notice.scheduledAt).toLocaleString()}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded p-1 opacity-70 hover:opacity-100"
        aria-label="Dismiss notice"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
